import React, { useState, useEffect } from 'react';
import { FiUser, FiMail, FiPhone, FiMapPin, FiCheck, FiCalendar, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface ProfileForm {
  nome: string;
  email: string;
  telemovel: string;
  morada: string;
  dataNascimento: string;
}

const ForceProfileUpdate: React.FC = () => {
  const { user, cliente, updateCliente, profileUpdateRequired, clearProfileUpdateRequired } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState<ProfileForm>({
    nome: '',
    email: '',
    telemovel: '',
    morada: '',
    dataNascimento: '',
  });

  useEffect(() => {
    if (profileUpdateRequired) {
      setForm({
        nome: cliente?.NOME || user?.name || '',
        email: user?.email?.endsWith('@pending.pagali.cv') ? '' : (user?.email || ''),
        telemovel: cliente?.TELEFONE || cliente?.TELEMOVEL || '',
        morada: cliente?.MORADA || '',
        dataNascimento: cliente?.DATA || '',
      });
    }
  }, [profileUpdateRequired, user, cliente]);

  if (!profileUpdateRequired) return null;

  const isFormValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return (
      form.nome.trim().length >= 3 &&
      emailRegex.test(form.email) &&
      form.telemovel.trim().length >= 7
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) return;
    setLoading(true);
    setError('');

    try {
      await api.put('/client/profile', {
        nome: form.nome.trim(),
        email: form.email.trim(),
        telemovel: form.telemovel.trim(),
        morada: form.morada.trim(),
        dataNascimento: form.dataNascimento.trim(),
      });

      updateCliente({
        NOME: form.nome.trim(),
        EMAIL: form.email.trim(),
        MORADA: form.morada.trim(),
        TELEFONE: form.telemovel.trim(),
      });

      setSuccess(true);
      setTimeout(() => clearProfileUpdateRequired(), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao atualizar perfil. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    // Backdrop não clicável — utilizador não pode fechar sem preencher
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-pagali-primary to-purple-700 px-6 py-5 text-white">
          <div className="flex items-center gap-3 mb-1">
            <FiAlertCircle className="text-2xl flex-shrink-0" />
            <h2 className="text-xl font-bold">Complete os seus dados</h2>
          </div>
          <p className="text-sm opacity-90 ml-9">
            Para continuar a usar o Pagali, precisa de preencher os seus dados obrigatórios.
          </p>
        </div>

        {success ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCheck className="text-3xl text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Dados atualizados!</h3>
            <p className="text-sm text-gray-500 mt-1">Obrigado. Pode agora utilizar todas as funcionalidades do Pagali.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
                <FiAlertCircle className="flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome completo <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pagali-primary/30 focus:border-pagali-primary outline-none transition-all"
                  placeholder="O seu nome completo"
                  required
                  minLength={3}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pagali-primary/30 focus:border-pagali-primary outline-none transition-all"
                  placeholder="seuemail@exemplo.com"
                  required
                />
              </div>
            </div>

            {/* Telemovel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telemóvel <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  value={form.telemovel}
                  onChange={(e) => setForm({ ...form, telemovel: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pagali-primary/30 focus:border-pagali-primary outline-none transition-all"
                  placeholder="+238 9XX XXXX"
                  required
                  minLength={7}
                />
              </div>
            </div>

            {/* Data de Nascimento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de nascimento
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={form.dataNascimento}
                  onChange={(e) => setForm({ ...form, dataNascimento: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pagali-primary/30 focus:border-pagali-primary outline-none transition-all"
                />
              </div>
            </div>

            {/* Morada */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Morada
              </label>
              <div className="relative">
                <FiMapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={form.morada}
                  onChange={(e) => setForm({ ...form, morada: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pagali-primary/30 focus:border-pagali-primary outline-none transition-all"
                  placeholder="A sua morada"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={!isFormValid() || loading}
              className={`w-full py-3 rounded-lg font-semibold text-white transition-all ${
                isFormValid() && !loading
                  ? 'bg-pagali-primary hover:bg-pagali-primary/90 shadow-lg shadow-pagali-primary/25'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  A guardar...
                </span>
              ) : (
                'Guardar e continuar'
              )}
            </button>

            <p className="text-xs text-gray-400 text-center">
              Os campos marcados com <span className="text-red-500">*</span> são obrigatórios para realizar pagamentos
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForceProfileUpdate;
